import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import PropTypes from 'prop-types';

const jobTitleLabels = {
  director: 'Diretor',
  manager: 'Gerente',
  analyst: 'Analista',
  intern: 'Estagiário',
  trainer: 'Treinador',
  planner: 'Planejador',
};

const permissionStyles = {
  write: {
    label: 'Editar',
    container: 'border-permissionEditBorder bg-permissionEditBg text-blueDark',
    dot: 'bg-permissionEditDot',
    option: 'border-transparent hover:bg-permissionEditBg',
    selected: 'border-permissionEditBorder bg-permissionEditBg',
    check: 'text-permissionEditDot',
  },
  read: {
    label: 'Visualizar',
    container: 'border-permissionReadBorder bg-permissionReadBg text-blueDark',
    dot: 'bg-permissionReadDot',
    option: 'border-transparent hover:bg-permissionReadBg',
    selected: 'border-permissionReadBorder bg-permissionReadBg',
    check: 'text-permissionReadDot',
  },
  none: {
    label: 'Sem acesso',
    container: 'border-permissionNoneBorder bg-permissionNoneBg text-blueDark',
    dot: 'bg-permissionNoneDot',
    option: 'border-transparent hover:bg-permissionNoneBg',
    selected: 'border-permissionNoneBorder bg-permissionNoneBg',
    check: 'text-permissionNoneDot',
  },
};

const permissionOptions = ['write', 'read', 'none'];

const PermissionSelect = ({ value, onChange, label, disabled }) => {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const current = permissionStyles[value] || permissionStyles.none;

  const closeMenu = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) buttonRef.current?.focus();
  };

  const openMenu = () => {
    if (disabled || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const estimatedMenuHeight = 146;
    const opensUpward = window.innerHeight - rect.bottom < estimatedMenuHeight + 12;

    const desiredTop = opensUpward ? rect.top - estimatedMenuHeight - 8 : rect.bottom + 8;

    setMenuPosition({
      left: Math.max(12, Math.min(rect.left, window.innerWidth - rect.width - 12)),
      top: Math.max(12, Math.min(desiredTop, window.innerHeight - estimatedMenuHeight - 12)),
      width: rect.width,
    });
    setOpen(true);
  };

  const toggleMenu = () => {
    if (open) closeMenu();
    else openMenu();
  };

  useEffect(() => {
    if (!open) return undefined;

    const selectedOption = menuRef.current?.querySelector(`[data-permission="${value}"]`);
    selectedOption?.focus();

    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      closeMenu();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu(true);
    };
    const handleViewportChange = () => closeMenu();

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [open, value]);

  const handleOptionKeyDown = (event, optionIndex) => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;

    event.preventDefault();
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (optionIndex + direction + permissionOptions.length) % permissionOptions.length;
    menuRef.current?.querySelector(`[data-permission="${permissionOptions[nextIndex]}"]`)?.focus();
  };

  const selectOption = (nextValue) => {
    onChange(nextValue);
    closeMenu(true);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={toggleMenu}
        onKeyDown={(event) => {
          if (['ArrowDown', 'ArrowUp'].includes(event.key) && !open) {
            event.preventDefault();
            openMenu();
          }
        }}
        style={{ minWidth: '158px' }}
        className={`group relative inline-flex items-center gap-2 rounded-full border px-3 py-2 text-left shadow-sm transition-all duration-200 ${
          current.container
        } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:-translate-y-px hover:shadow-md'}`}
      >
        <span className={`flex-none rounded-full shadow-sm ${current.dot}`} style={{ width: '12px', height: '12px' }} />
        <span className="flex-1 font-medium" style={{ fontSize: '13px' }}>
          {current.label}
        </span>
        <ChevronDown
          className={`flex-none text-blueSteel transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          size={16}
          strokeWidth={1.8}
        />
      </button>

      {open &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={label}
            className="fixed z-50 rounded-2xl border border-grayExtraLight2 bg-white p-1.5 shadow-xl flex flex-col gap-1.5"
            style={menuPosition}
          >
            {permissionOptions.map((optionValue, optionIndex) => {
              const option = permissionStyles[optionValue];
              const selected = optionValue === value;

              return (
                <button
                  key={optionValue}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  data-permission={optionValue}
                  onClick={() => selectOption(optionValue)}
                  onKeyDown={(event) => handleOptionKeyDown(event, optionIndex)}
                  className={`flex w-full items-center gap-2.5 rounded-xl border px-3 py-3 text-left text-xs font-medium text-blueDark transition-colors ${
                    selected ? option.selected : option.option
                  }`}
                >
                  <span className={`h-2.5 w-2.5 flex-none rounded-full ${option.dot}`} />
                  <span className="flex-1">{option.label}</span>
                  <span className="flex h-4 w-4 items-center justify-center">
                    {selected && <Check className={option.check} size={14} strokeWidth={2.5} />}
                  </span>
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

PermissionSelect.propTypes = {
  value: PropTypes.oneOf(['none', 'read', 'write']).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

PermissionSelect.defaultProps = {
  disabled: false,
};

const profileOrder = ['director', 'manager', 'analyst', 'intern', 'trainer', 'planner'];

const PermissionMatrix = ({
  areaLabel,
  hierarchy,
  profiles,
  features,
  onPermissionChange,
  disabledFeatureKeys,
  useWildcardFallback,
}) => {
  const orderedProfiles = [...profiles].sort((left, right) => {
    const leftIndex = profileOrder.indexOf(left.jobTitle);
    const rightIndex = profileOrder.indexOf(right.jobTitle);
    return leftIndex - rightIndex || left.name.localeCompare(right.name);
  });

  if (orderedProfiles.length === 0) {
    return (
      <div className="flex h-56 items-center justify-center rounded-xl border border-grayExtraLight2 text-sm text-blueSteel">
        Nenhum perfil cadastrado para a área de {areaLabel.toLowerCase()}.
      </div>
    );
  }

  const minWidth = 260 + orderedProfiles.length * 190;
  const columns = `minmax(240px, 1.2fr) repeat(${orderedProfiles.length}, minmax(180px, 1fr))`;

  return (
    <div>
      <div className="flex justify-between items-center mb-7 mt-9" style={{ fontSize: '13px' }}>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-blueSteel">
          <span className="flex items-center gap-2">
            <span className="rounded-full bg-permissionEditDot" style={{ width: '9px', height: '9px' }} /> Editar
          </span>
          <span className="flex items-center gap-2">
            <span className="rounded-full bg-permissionReadDot" style={{ width: '9px', height: '9px' }} /> Visualizar
          </span>
          <span className="flex items-center gap-2">
            <span className="rounded-full bg-permissionNoneDot" style={{ width: '9px', height: '9px' }} /> Sem acesso
          </span>
        </div>
        <div className="flex justify-end text-blueSteel items-center">
          <span className="font-semibold text-blueDark">Hierarquia do {areaLabel}:</span>
          <span className="ml-2">{hierarchy.join('  ›  ')}</span>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-grayExtraLight2">
        <div style={{ minWidth }}>
          <div className="grid items-center bg-blueLight px-5 py-4" style={{ gridTemplateColumns: columns }}>
            <span className="text-sm font-semibold text-blueDarker">Módulo</span>
            {orderedProfiles.map((profile) => (
              <span key={profile.id} className="px-3 text-center text-sm font-semibold text-blueDarker">
                {jobTitleLabels[profile.jobTitle] || profile.name}
              </span>
            ))}
          </div>

          {features.map((feature) => (
            <div
              key={feature.featureKey}
              className="grid min-h-custom60 items-center border-t border-grayExtraLight2 px-5 py-2 first:border-t-0"
              style={{ gridTemplateColumns: columns }}
            >
              <span className="pr-4 text-base text-blueDarker">{feature.fullLabel}</span>
              {orderedProfiles.map((profile) => {
                const directPermission = profile.permissions.find((item) => item.featureKey === feature.featureKey);
                const wildcardPermission = useWildcardFallback
                  ? profile.permissions.find((item) => item.featureKey === '*')
                  : null;
                const disabled = disabledFeatureKeys.includes(feature.featureKey);
                const configuredLevel =
                  directPermission?.level || wildcardPermission?.level || (useWildcardFallback ? 'write' : 'none');
                const level = disabled ? 'write' : configuredLevel;

                return (
                  <div key={profile.id} className="flex justify-center px-3">
                    <PermissionSelect
                      value={level}
                      disabled={disabled}
                      label={`${feature.fullLabel} para ${jobTitleLabels[profile.jobTitle] || profile.name}`}
                      onChange={(nextLevel) => onPermissionChange(profile.id, feature.featureKey, nextLevel)}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

PermissionMatrix.propTypes = {
  areaLabel: PropTypes.string.isRequired,
  hierarchy: PropTypes.arrayOf(PropTypes.string).isRequired,
  profiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      jobTitle: PropTypes.string,
      permissions: PropTypes.arrayOf(
        PropTypes.shape({
          featureKey: PropTypes.string.isRequired,
          level: PropTypes.oneOf(['none', 'read', 'write']).isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  features: PropTypes.arrayOf(
    PropTypes.shape({
      featureKey: PropTypes.string.isRequired,
      fullLabel: PropTypes.string.isRequired,
    })
  ).isRequired,
  onPermissionChange: PropTypes.func.isRequired,
  disabledFeatureKeys: PropTypes.arrayOf(PropTypes.string),
  useWildcardFallback: PropTypes.bool,
};

PermissionMatrix.defaultProps = {
  disabledFeatureKeys: [],
  useWildcardFallback: false,
};

export default PermissionMatrix;
